
import React, { useState } from 'react';
import { IconChartBar, IconPresentationChart, IconBadgeCheck, IconSparkles, IconQuant } from '../components/Icons';
import { analyzeTradePerformance } from '../services/geminiService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, ReferenceLine } from 'recharts';
import toast from 'react-hot-toast';

interface TradeExecution {
    id: string;
    symbol: string;
    side: 'Buy' | 'Sell';
    size: number;
    arrivalPx: number;
    execPx: number;
    slippage: number; // bps
    broker: string;
    algo: string;
}

const MOCK_EXECUTIONS: TradeExecution[] = [
    { id: 'EX-101', symbol: 'NVDA', side: 'Buy', size: 5000, arrivalPx: 880.00, execPx: 880.45, slippage: 5.1, broker: 'Goldman Sachs', algo: 'VWAP' },
    { id: 'EX-102', symbol: 'AAPL', side: 'Sell', size: 12000, arrivalPx: 175.20, execPx: 175.22, slippage: -1.1, broker: 'Morgan Stanley', algo: 'POV' }, // Negative slippage on sell is bad? No, usually slippage = (Exec - Arrival) * Side. Buy: 880.45 - 880 = +0.45 (Cost). Sell: 175.22 - 175.20 = +0.02 (Improvement). Let's standardize: Positive = Cost (Bad), Negative = Improvement (Good).
    // Actually standard is: Buy: P_exec - P_arrival. Sell: P_arrival - P_exec.
    // NVDA Buy: 880.45 - 880.00 = +0.45 (Cost). 5.1 bps.
    // AAPL Sell: 175.20 - 175.22 = -0.02 (Gain/Improvement). -1.1 bps.
    { id: 'EX-103', symbol: 'MSFT', side: 'Buy', size: 2000, arrivalPx: 415.00, execPx: 415.10, slippage: 2.4, broker: 'J.P. Morgan', algo: 'Arrival' },
    { id: 'EX-104', symbol: 'TSLA', side: 'Sell', size: 5000, arrivalPx: 178.00, execPx: 177.50, slippage: 28.0, broker: 'Citi', algo: 'Market' }, // Huge slippage
    { id: 'EX-105', symbol: 'AMD', side: 'Buy', size: 3000, arrivalPx: 180.00, execPx: 179.95, slippage: -2.8, broker: 'Goldman Sachs', algo: 'TWAP' },
];

const TCAView: React.FC = () => {
    const [executions, setExecutions] = useState<TradeExecution[]>(MOCK_EXECUTIONS);
    const [aiCoaching, setAiCoaching] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Broker Stats
    const brokerStats = React.useMemo(() => {
        const stats: Record<string, { totalSlip: number, count: number }> = {};
        executions.forEach(ex => {
            if (!stats[ex.broker]) stats[ex.broker] = { totalSlip: 0, count: 0 };
            stats[ex.broker].totalSlip += ex.slippage;
            stats[ex.broker].count += 1;
        });
        return Object.keys(stats).map(b => ({
            broker: b,
            avgSlippage: stats[b].totalSlip / stats[b].count
        })).sort((a, b) => a.avgSlippage - b.avgSlippage);
    }, [executions]);

    const handleAnalyze = async () => {
        setIsAnalyzing(true);
        setAiCoaching('');
        
        const coaching = await analyzeTradePerformance(executions);
        setAiCoaching(coaching);
        setIsAnalyzing(false);
    };

    return (
        <div className="flex h-full bg-slate-100">
            <div className="max-w-6xl mx-auto w-full p-8 flex flex-col space-y-6 overflow-y-auto custom-scrollbar">
                
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <IconPresentationChart /> Transaction Cost Analysis (TCA)
                        </h2>
                        <p className="text-slate-500 text-sm">Post-Trade Execution Quality & Broker Review</p>
                    </div>
                    <button 
                        onClick={handleAnalyze}
                        disabled={isAnalyzing}
                        className="bg-slate-900 text-white px-4 py-2 rounded shadow-md text-sm font-bold hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                        {isAnalyzing ? <span className="animate-spin">⏳</span> : <IconSparkles />}
                        Run AI Coach
                    </button>
                </div>

                {/* AI Insight Card */}
                {aiCoaching && (
                    <div className="bg-white border-l-4 border-purple-500 p-6 rounded-r-lg shadow-sm flex items-start gap-4 animate-fade-in-up">
                        <div className="text-purple-600 pt-1"><IconQuant /></div>
                        <div>
                            <h3 className="font-bold text-purple-800 text-sm uppercase mb-1">AiQ Trading Coach</h3>
                            <p className="text-slate-700 leading-relaxed text-sm">{aiCoaching}</p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Slippage Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <IconChartBar /> Implementation Shortfall (bps)
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={executions}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="symbol" fontSize={12} stroke="#94a3b8" />
                                    <YAxis fontSize={12} stroke="#94a3b8" label={{ value: 'Slippage (bps)', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip 
                                        cursor={{fill: '#f8fafc'}}
                                        contentStyle={{ borderRadius: '8px' }}
                                        formatter={(val: number) => `${val.toFixed(1)} bps`} 
                                    />
                                    <ReferenceLine y={0} stroke="#cbd5e1" />
                                    <Bar dataKey="slippage" radius={[4, 4, 0, 0]}>
                                        {executions.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.slippage > 0 ? '#ef4444' : '#10b981'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Broker Scorecard */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <IconBadgeCheck /> Broker Scorecard
                        </h3>
                        <div className="overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 text-slate-500 font-bold">
                                    <tr>
                                        <th className="px-4 py-2">Broker</th>
                                        <th className="px-4 py-2 text-right">Avg Slippage</th>
                                        <th className="px-4 py-2">Rating</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {brokerStats.map(b => (
                                        <tr key={b.broker}>
                                            <td className="px-4 py-3 font-medium text-slate-700">{b.broker}</td>
                                            <td className={`px-4 py-3 text-right font-mono font-bold ${b.avgSlippage > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {b.avgSlippage > 0 ? '+' : ''}{b.avgSlippage.toFixed(1)} bps
                                            </td>
                                            <td className="px-4 py-3">
                                                {b.avgSlippage < 0 ? '⭐⭐⭐⭐⭐' : b.avgSlippage < 2 ? '⭐⭐⭐⭐' : b.avgSlippage < 5 ? '⭐⭐⭐' : '⭐⭐'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Trade Tape */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                        <h3 className="font-bold text-slate-800">Execution Tape</h3>
                    </div>
                    <table className="w-full text-sm text-left">
                        <thead className="bg-white text-slate-500 border-b border-slate-100">
                            <tr>
                                <th className="px-6 py-3">ID</th>
                                <th className="px-6 py-3">Symbol</th>
                                <th className="px-6 py-3">Side</th>
                                <th className="px-6 py-3 text-right">Arrival Px</th>
                                <th className="px-6 py-3 text-right">Exec Px</th>
                                <th className="px-6 py-3 text-right">Cost ($)</th>
                                <th className="px-6 py-3">Algo</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {executions.map(ex => {
                                // Calculate Dollar Cost: (Slippage Bps / 10000) * Notional
                                const cost = (ex.slippage / 10000) * (ex.size * ex.execPx);
                                return (
                                    <tr key={ex.id} className="hover:bg-slate-50">
                                        <td className="px-6 py-3 text-slate-500 font-mono text-xs">{ex.id}</td>
                                        <td className="px-6 py-3 font-bold text-slate-800">{ex.symbol}</td>
                                        <td className={`px-6 py-3 font-bold ${ex.side === 'Buy' ? 'text-blue-600' : 'text-purple-600'}`}>{ex.side}</td>
                                        <td className="px-6 py-3 text-right font-mono text-slate-600">{ex.arrivalPx.toFixed(2)}</td>
                                        <td className="px-6 py-3 text-right font-mono text-slate-800">{ex.execPx.toFixed(2)}</td>
                                        <td className={`px-6 py-3 text-right font-bold ${cost > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                            {cost > 0 ? '-' : '+'}${Math.abs(cost).toLocaleString(undefined, {maximumFractionDigits:0})}
                                        </td>
                                        <td className="px-6 py-3 text-xs bg-slate-50 rounded">{ex.algo}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
};

export default TCAView;
